import { useState, useEffect, useRef } from 'react'
import { SHOWCASE } from '@/constants'

// Hold-then-slide marquee (matches the reference): each screenshot holds
// still in the center for HOLD_MS, then slides one step over SLIDE_MS with
// an ease-out curve, then holds again. Step distance is measured live from
// the actual rendered item positions, so it stays correct at every
// breakpoint/image size automatically — nothing hardcoded.
const HOLD_MS = 1900
const SLIDE_MS = 450

const MIN_COPIES = 2

export default function AppShowcase() {
  const trackRef = useRef(null)
  const wrapRef  = useRef(null)
  const [copies, setCopies] = useState(MIN_COPIES)
  const offsetRef  = useRef(0)
  const pausedRef  = useRef(false)
  const rafRef     = useRef(null)
  const lastTsRef  = useRef(null)
  const phaseRef      = useRef('hold')   // 'hold' | 'sliding'
  const phaseStartRef = useRef(null)
  const slideFromRef   = useRef(0)
  const slideTargetRef = useRef(0)
  const stepWidthRef   = useRef(0)

  // Slide list repeated `copies` times so the track can scroll seamlessly
  // and always stays wider than the container (see the effect below).
  const slides = Array.from({ length: copies }, () => SHOWCASE).flat()

  // Ensures the track is always wider than its container — at any zoom
  // level or screen width — so the loop never runs out of content and
  // shows a gap before wrapping. Re-checks whenever `copies` changes,
  // converging once there's enough (only grows, never shrinks).
  useEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current
      const track = trackRef.current
      if (!wrap || !track) return
      const oneSetWidth = track.scrollWidth / copies
      if (oneSetWidth <= 0) return
      const needed = Math.ceil(wrap.clientWidth / oneSetWidth) + 1
      if (needed > copies) setCopies(needed)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [copies])

  useEffect(() => {
    const track = trackRef.current
    const wrap  = wrapRef.current
    if (!track || !wrap) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const applyScale = () => {
      const wrapRect = wrap.getBoundingClientRect()
      const center = wrapRect.left + wrapRect.width / 2
      const items = track.querySelectorAll('.app-showcase-item')
      items.forEach((item) => {
        const r = item.getBoundingClientRect()
        const itemCenter = r.left + r.width / 2
        const dist = Math.abs(itemCenter - center)
        const closeness = Math.max(0, 1 - dist / (wrapRect.width / 2))
        const scale = 0.72 + closeness * 0.53   // 0.72 → 1.25
        const opacity = 0.35 + closeness * 0.65 // 0.35 → 1
        const blur = (1 - closeness) * 3.5      // 3.5px → 0px
        item.style.transform = `scale(${scale.toFixed(3)})`
        item.style.opacity = opacity.toFixed(3)
        item.style.filter = `blur(${blur.toFixed(2)}px)`
        item.style.zIndex = Math.round(closeness * 100)
      })
    }

    if (prefersReducedMotion) {
      applyScale()
      return
    }

    const loopWidth = () => track.scrollWidth / copies

    // The step is derived directly from the authoritative loop width
    // instead of measuring two DOM items live every slide. This
    // guarantees 5 steps always add up to *exactly* one full loop, so
    // the wrap-around at the seam is pixel-perfect instead of drifting
    // from tiny sub-pixel rounding differences between live measurements.
    const computeStepWidth = () => {
      stepWidthRef.current = loopWidth() / SHOWCASE.length
    }
    computeStepWidth()
    window.addEventListener('resize', computeStepWidth)

    const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3)

    const tick = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts
      lastTsRef.current = ts
      if (phaseStartRef.current == null) phaseStartRef.current = ts

      if (!pausedRef.current) {
        const elapsed = ts - phaseStartRef.current

        if (phaseRef.current === 'hold') {
          if (elapsed >= HOLD_MS) {
            slideFromRef.current = offsetRef.current
            slideTargetRef.current = offsetRef.current + stepWidthRef.current
            phaseRef.current = 'sliding'
            phaseStartRef.current = ts
          }
        } else {
          const progress = Math.min(elapsed / SLIDE_MS, 1)
          const eased = easeOutCubic(progress)
          offsetRef.current = slideFromRef.current + (slideTargetRef.current - slideFromRef.current) * eased

          if (progress >= 1) {
            const width = loopWidth()
            if (width > 0 && offsetRef.current >= width) {
              offsetRef.current -= width
            }
            phaseRef.current = 'hold'
            phaseStartRef.current = ts
          }
        }

        track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`
      }

      applyScale()
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
    cancelAnimationFrame(rafRef.current);
    window.removeEventListener('resize', computeStepWidth)
};
  }, [copies])

  return (
    <section
      className="app-showcase-section"
      aria-label="App screenshots"
    >
      <div className="app-showcase-wrap" ref={wrapRef}>
        <div className="app-showcase-track" ref={trackRef}>
          {slides.map((slide, i) => {
            const waveIndex = i % SHOWCASE.length % 3
            return (
              <div
                className={`app-showcase-item wave-${waveIndex}`}
                key={`${slide.id}-${i}`}
                aria-hidden={i >= SHOWCASE.length}
              >
                <div className="app-showcase-phone">
                  <img src={slide.image} alt="" loading="lazy" />
                </div>
                <span className="app-showcase-caption">{slide.label}</span>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}